
import React, { useState } from 'react';

interface Tool {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: string;
  color: string;
  roles: string[];
  status: 'active' | 'beta' | 'coming';
}

interface ToolGridProps {
  selectedCategory: string;
  userRole: string;
  onOpenCozeChat?: () => void;
}

export default function ToolGrid({ selectedCategory, userRole, onOpenCozeChat }: ToolGridProps) {
  // 移除弹窗相关的状态，因为不再需要
  // const [isStrategicModalOpen, setIsStrategicModalOpen] = useState(false);

  const tools: Tool[] = [
    {
      id: 'idste',
      name: '战略解码系统',
      description: '从愿景到员工PBC的穿透式分解',
      icon: 'ri-target-line',
      category: 'system',
      color: 'bg-blue-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },
    {
      id: 'coze-chat',
      name: 'Coze AI 对话',
      description: '智能AI助手，提供专业的对话服务',
      icon: 'ri-robot-line',
      category: 'efficiency',
      color: 'bg-gradient-to-r from-blue-500 to-purple-600',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },
    {
      id: 'performance',
      name: '绩效管理体系',
      description: '动态目标设定、实时反馈、360度评估',
      icon: 'ri-bar-chart-line',
      category: 'system',
      color: 'bg-green-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },
    {
      id: 'training',
      name: '培训管理体系',
      description: '智能推荐培训课程，链接战略能力缺口',
      icon: 'ri-graduation-cap-line',
      category: 'system',
      color: 'bg-purple-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },
    {
      id: 'health',
      name: '组织健康度看板',
      description: '监控员工敬业度、部门协作效率',
      icon: 'ri-heart-pulse-line',
      category: 'system',
      color: 'bg-red-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },
    {
      id: 'compliance',
      name: '合规与风控中枢',
      description: '自动审计与预警，内置各类合规要求',
      icon: 'ri-shield-check-line',
      category: 'system',
      color: 'bg-orange-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },

    // 场景工具
    {
      id: 'sales',
      name: '销售日历',
      description: '整合客户接触点、商机阶段与战略目标',
      icon: 'ri-calendar-check-line',
      category: 'scenario',
      color: 'bg-cyan-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },
    {
      id: 'change',
      name: '变革管理工具',
      description: '可视化变革进程，管理变革阻力',
      icon: 'ri-exchange-line',
      category: 'scenario',
      color: 'bg-indigo-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'beta',
    },
    {
      id: 'issue',
      name: '问题管理法',
      description: '结构化问题录入、归因、分配与跟踪',
      icon: 'ri-question-answer-line',
      category: 'scenario',
      color: 'bg-yellow-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },
    {
      id: 'milestone',
      name: '项目里程碑管理',
      description: '跨部门项目自动对齐战略节点',
      icon: 'ri-flag-line',
      category: 'scenario',
      color: 'bg-pink-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },

    // 效率工具
    {
      id: 'todo',
      name: '智能待办引擎',
      description: '智能优先级排序，提升工作效率',
      icon: 'ri-task-line',
      category: 'efficiency',
      color: 'bg-teal-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },
    {
      id: 'learning',
      name: '个人学习地图',
      description: '个性化学习路径规划和推荐',
      icon: 'ri-map-line',
      category: 'efficiency',
      color: 'bg-violet-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'active',
    },
    {
      id: 'report',
      name: '日报周报自动化',
      description: '自动生成工作报告，节省时间',
      icon: 'ri-file-text-line',
      category: 'efficiency',
      color: 'bg-emerald-500',
      roles: [
        '战略管理',
        '经营管理负责人',
        '运营负责人',
        '经理',
        '员工',
        'admin',
        'manager',
        'employee',
      ],
      status: 'beta',
    },
  ];

  const filteredTools = tools.filter((tool) => {
    const categoryMatch = selectedCategory === 'all' || tool.category === selectedCategory;
    // 确保所有角色都能看到所有工具
    const currentRole = userRole || 'employee';
    const roleMatch = tool.roles.includes(currentRole);
    return categoryMatch && roleMatch;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'beta':
        return (
          <span className="absolute top-2 right-2 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
            Beta
          </span>
        );
      case 'coming':
        return (
          <span className="absolute top-2 right-2 bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full">
            即将上线
          </span>
        );
      default:
        return null;
    }
  };

  const handleToolClick = (toolId: string) => {
    if (toolId === 'idste') {
      // 使用 navigate 替代 window.open，确保开发环境正常工作
      const userName = localStorage.getItem('userName') || '';
      const userId = localStorage.getItem('userId') || '';

      const params = new URLSearchParams({
        tool: 'strategic-decode-system',
        toolName: '战略解码系统',
        function: 'main',
        functionName: '主功能',
        user: userName,
        role: userRole,
        userId,
        timestamp: new Date().toISOString(),
        action: 'open-tool',
      });

      // 改为在当前窗口打开，避免开发环境的新窗口问题
      window.location.href = `/tool-detail?${params.toString()}`;
    } else if (toolId === 'coze-chat') {
      // 打开 Coze AI 对话
      if (onOpenCozeChat) {
        onOpenCozeChat();
      }
    } else {
      // 其他工具也使用当前窗口打开
      const userName = localStorage.getItem('userName') || '';
      const userId = localStorage.getItem('userId') || '';

      const tool = tools.find((t) => t.id === toolId);
      if (tool) {
        const params = new URLSearchParams({
          tool: toolId,
          toolName: tool.name,
          function: 'main',
          functionName: '主功能',
          user: userName,
          role: userRole,
          userId,
          timestamp: new Date().toISOString(),
          action: 'open-tool',
        });

        // 使用当前窗口而不是新窗口
        window.location.href = `/tool-detail?${params.toString()}`;
      }
    }
  };

  // Helper to derive category label & background classes
  const getCategoryMeta = (category: string) => {
    switch (category) {
      case 'system':
        return { label: '系统工具', className: 'bg-blue-100 text-blue-800' };
      case 'scenario':
        return { label: '场景工具', className: 'bg-green-100 text-green-800' };
      default:
        // 'efficiency' or any other future category
        return { label: '效率工具', className: 'bg-purple-100 text-purple-800' };
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">
          {selectedCategory === 'all'
            ? '全部工具'
            : selectedCategory === 'system'
            ? '系统工具'
            : selectedCategory === 'scenario'
            ? '场景工具'
            : '效率工具'}
        </h2>
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <span>共 {filteredTools.length} 个工具</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTools.map((tool) => {
          const { label: categoryLabel, className: categoryClass } = getCategoryMeta(
            tool.category
          );
          return (
            <div
              key={tool.id}
              className="relative bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow cursor-pointer group"
              onClick={() => handleToolClick(tool.id)}
            >
              {getStatusBadge(tool.status)}

              <div className="flex items-start space-x-4">
                <div className={`w-12 h-12 ${tool.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <i className={`${tool.icon} text-white text-xl`}></i>
                </div>

                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-indigo-600 transition-colors">
                    {tool.name}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{tool.description}</p>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${categoryClass}`}
                  >
                    {categoryLabel}
                  </span>
                </div>

                <button
                  className="text-indigo-600 hover:text-indigo-700 font-medium text-sm whitespace-nowrap cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToolClick(tool.id);
                  }}
                >
                  打开应用 <i className="ri-arrow-right-line ml-1"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {filteredTools.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-apps-line text-2xl text-gray-400"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">暂无可用工具</h3>
          <p className="text-gray-600">
            当前角色({userRole || '未知'})暂无此分类下的可用工具
          </p>
        </div>
      )}

      {/* 移除战略解码系统功能选择弹窗，因为不再需要 */}
    </div>
  );
}
